// src/contexts/servicos/EquipamentoClienteContext.js
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import EquipamentoClienteCad from '../../pages/servicos/cadastros/EquipamentoClienteCad';
import EquipamentoClienteSearch from '../../pages/servicos/cadastros/EquipamentoClienteSearch';
import { deleteData, fetchData, postData, putData } from '../../services/Api';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';

const EquipamentoClienteContext = createContext();

export const useEquipamentoCliente = () => {
  const context = useContext(EquipamentoClienteContext);
  if (!context) {
    throw new Error('useEquipamentoCliente deve ser usado dentro de EquipamentoClienteProvider.');
  }
  return context;
};

export const EquipamentoClienteProvider = ({ children }) => {
  const [equipamentos, setEquipamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showCad, setShowCad] = useState(false);
  const [equipamentoInicial, setEquipamentoInicial] = useState(null);
  const [clienteInicial, setClienteInicial] = useState(null);
  const [clienteIdSearch, setClienteIdSearch] = useState(null);
  const [fecharCadAoSalvar, setFecharCadAoSalvar] = useState(false);
  const onSelectRef = useRef(null);
  const onSaveRef = useRef(null);
  const { showAlert } = useAlert();
  const { showMessage } = useMessage();

  const executar = useCallback(async (acao, mensagemSucesso, mensagemErro) => {
    setLoading(true);
    try {
      const result = await acao();
      if (mensagemSucesso) showAlert(mensagemSucesso);
      return { sucesso: true, dados: result };
    } catch (error) {
      console.error(mensagemErro, error);
      showMessage(`${mensagemErro}\n${error?.message || error}`, 'danger');
      return { sucesso: false, dados: null };
    } finally {
      setLoading(false);
    }
  }, [showAlert, showMessage]);

  const listarEquipamentos = useCallback(async (clienteId, ativo) => {
    const resultado = await executar(
      () => fetchData('/EquipamentoCliente', { clienteId, ativo }),
      null,
      'Erro ao buscar equipamentos dos clientes!',
    );
    if (!resultado.sucesso) return null;
    setEquipamentos(resultado.dados || []);
    return resultado.dados || [];
  }, [executar]);

  const adicionarEquipamento = async equipamento => {
    const resultado = await executar(
      () => postData('/EquipamentoCliente', equipamento),
      'Equipamento adicionado com sucesso.',
      'Erro ao adicionar equipamento!',
    );
    if (!resultado.sucesso) return null;
    setEquipamentos(anteriores => [...anteriores, resultado.dados]);
    return resultado.dados;
  };

  const atualizarEquipamento = async equipamento => {
    const resultado = await executar(
      () => putData(`/EquipamentoCliente/${equipamento.id}`, equipamento),
      'Equipamento alterado com sucesso.',
      'Erro ao alterar equipamento!',
    );
    if (!resultado.sucesso) return null;
    setEquipamentos(anteriores => anteriores.map(
      item => item.id === resultado.dados.id ? resultado.dados : item,
    ));
    return resultado.dados;
  };

  const excluirEquipamento = async id => {
    const resultado = await executar(
      () => deleteData('/EquipamentoCliente', { equipamentoClienteId: id }),
      'Equipamento excluído com sucesso.',
      'Erro ao excluir equipamento!',
    );
    if (!resultado.sucesso) return false;
    setEquipamentos(anteriores => anteriores.filter(item => item.id !== id));
    return true;
  };

  const handleShowEquipamentoClienteSearch = async clienteId => {
    await listarEquipamentos(clienteId, clienteId ? true : undefined);
    setClienteIdSearch(clienteId || null);
    return new Promise(resolve => {
      onSelectRef.current = resolve;
      setShowSearch(true);
    });
  };

  const fecharSearch = () => {
    setShowSearch(false);
    setClienteIdSearch(null);
    onSelectRef.current?.(null);
    onSelectRef.current = null;
  };

  const selecionarEquipamento = equipamento => {
    const resolver = onSelectRef.current;
    onSelectRef.current = null;
    setShowSearch(false);
    setClienteIdSearch(null);
    resolver?.(equipamento || null);
  };

  const showEquipamentoClienteCad = (equipamento = null, cliente = null, fecharAoSalvar = false) => new Promise(resolve => {
    setEquipamentoInicial(equipamento);
    setClienteInicial(cliente);
    setFecharCadAoSalvar(fecharAoSalvar);
    onSaveRef.current = resolve;
    setShowCad(true);
  });

  const fecharCad = () => {
    setShowCad(false);
    setEquipamentoInicial(null);
    setClienteInicial(null);
    setFecharCadAoSalvar(false);
    onSaveRef.current?.(null);
    onSaveRef.current = null;
  };

  const equipamentoSalvo = equipamento => {
    const resolver = onSaveRef.current;
    onSaveRef.current = null;
    resolver?.(equipamento || null);
    if (fecharCadAoSalvar) {
      setShowCad(false);
      setEquipamentoInicial(null);
      setClienteInicial(null);
      setFecharCadAoSalvar(false);
    }
  };

  const value = useMemo(() => ({
    equipamentos,
    loading,
    listarEquipamentos,
    adicionarEquipamento,
    atualizarEquipamento,
    excluirEquipamento,
    handleShowEquipamentoClienteSearch,
    showEquipamentoClienteCad,
  }), [equipamentos, loading, listarEquipamentos]);

  return (
    <EquipamentoClienteContext.Provider value={value}>
      {children}
      <EquipamentoClienteSearch
        show={showSearch}
        clienteId={clienteIdSearch}
        onHide={fecharSearch}
        onSelectEquipamentoCliente={selecionarEquipamento}
      />
      <EquipamentoClienteCad
        show={showCad}
        onHide={fecharCad}
        initialEquipamento={equipamentoInicial}
        initialCliente={clienteInicial}
        onSaveEquipamento={equipamentoSalvo}
      />
    </EquipamentoClienteContext.Provider>
  );
};
